import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeEntities, setAllEntities, setEntities, setEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, debounceTime, EMPTY, Observable, pipe, switchMap, tap } from 'rxjs';

import { DataUtil, isEqualSimpleObject } from '@celum/core';
import { createEmptyFormatFilter, Format, FormatFilters, FormatList, FormatTextFilterMatchMode } from '@celum/formats/domain';

import { FORMAT_SERVICE } from '../../services/formats.service';
import { NotificationService } from '../../services/notification.service';
import { getSpecificationTypeFilters } from '../../util/format-filters.util';

type FormatState = {
  loading: boolean;
  selectedFormatId: number;
  firstLoadCompleted: boolean;
  filters: FormatFilters;
  pagination: {
    totalCount: number;
    page: number;
    size: number;
    hasNext: boolean;
  };
};

const initialState: FormatState = {
  loading: true,
  selectedFormatId: null,
  firstLoadCompleted: false,
  filters: createEmptyFormatFilter(),
  pagination: {
    totalCount: 0,
    page: 0,
    size: 20,
    hasNext: false
  }
};

export const FormatStore = signalStore(
  withEntities<Format>(),
  withState(initialState),
  withComputed(({ selectedFormatId, entityMap }) => ({
    selectedFormat: computed(() => entityMap()[selectedFormatId()])
  })),
  withMethods((store, formatService = inject(FORMAT_SERVICE), notificationService = inject(NotificationService)) => {
    function loadFormatById(id: number): Observable<Format> {
      patchState(store, { loading: true });
      return formatService.getFormatById(id).pipe(
        tap({
          next: format => {
            patchState(store, setEntity(format));
            patchState(store, { loading: false });
          },
          error: error => {
            console.error('FormatStore: error on loading format by id', error.error);
            notificationService.error(error.error.errorMessage);
            patchState(store, { loading: false });
          }
        })
      );
    }

    function getSearchObservable(filters: FormatFilters, page: number, size: number): Observable<FormatList> {
      const sortBy = filters?.sort && filters.sort[0] ? filters.sort[0] : undefined;
      const sortDirection = filters?.sort && filters.sort[1] ? filters.sort[1] : undefined;
      let specificationTypeKeys: string[] = [];
      if (filters?.specificationTypes) {
        filters.specificationTypes.forEach(specificationType => {
          const filterValue = getSpecificationTypeFilters().find(it => it.key === specificationType).value;
          specificationTypeKeys = [...specificationTypeKeys, ...filterValue];
        });
      }

      return formatService
        .searchFormats({
          formatKeyFilter: { value: filters?.formatKey, matchMode: FormatTextFilterMatchMode.CONTAINS, ignoreCase: true },
          formatNameFilter: { value: filters?.formatName, matchMode: FormatTextFilterMatchMode.CONTAINS, ignoreCase: true },
          specificationTypeKeys,
          specificationKeys: DataUtil.isEmpty(filters?.specifications) ? null : filters.specifications,
          page: { page, size, sortBy, sortDirection }
        })
        .pipe(
          catchError(error => {
            console.error('FormatStore: error on loading formats', error.error);

            notificationService.error('FORMAT_LIST.SNACKBAR.LOAD_ERROR');
            return EMPTY;
          })
        );
    }

    const loadFormatsBatch = rxMethod<{ isFirstLoad: boolean; removeEntities: boolean }>(
      pipe(
        debounceTime(100),
        switchMap(obj => {
          patchState(store, { loading: true });

          if (obj.removeEntities) {
            patchState(store, removeEntities(Object.keys(store.entityMap())));
          }

          const { page, size } = store.pagination();
          const filters = store.filters();

          return getSearchObservable(filters, page, size).pipe(
            tapResponse({
              next: ({ content, hasNext, totalElements }) => {
                patchState(store, setEntities(content));
                patchState(store, {
                  firstLoadCompleted: obj.isFirstLoad ? true : store.firstLoadCompleted(),
                  loading: false,
                  pagination: {
                    ...store.pagination(),
                    totalCount: totalElements,
                    hasNext
                  }
                });
              },
              error: () => patchState(store, { loading: false }),
              finalize: () => patchState(store, { loading: false })
            })
          );
        })
      )
    );

    function loadNextFormatsBatch(): void {
      const { page } = store.pagination;

      patchState(store, { pagination: { ...store.pagination(), page: page() + 1 } });
      loadFormatsBatch({ isFirstLoad: false, removeEntities: false });
    }

    function updateBatchSize(size: number): void {
      patchState(store, { pagination: { ...store.pagination(), size } });
    }

    function updateSelectedFormatId(id: number): void {
      patchState(store, { selectedFormatId: id });
    }

    function updateFilters(filters: FormatFilters): void {
      const currentFilters = store.filters();
      if (isEqualSimpleObject(currentFilters, filters)) {
        return;
      }

      const batchSize = store.pagination.size();
      const firstLoadCompleted = store.firstLoadCompleted();

      patchState(store, {
        loading: true,
        firstLoadCompleted,
        filters,
        pagination: { ...store.pagination(), page: 0, size: batchSize }
      });

      loadFormatsBatch({ isFirstLoad: true, removeEntities: true });
    }

    function addFormat(format: Format): void {
      patchState(store, setAllEntities([format, ...store.entities()]));
      patchState(store, { pagination: { ...store.pagination(), totalCount: store.pagination.totalCount() + 1 } });
    }

    function updateFormat(format: Format): void {
      patchState(
        store,
        updateEntity({
          id: format.id,
          changes: format
        })
      );
    }

    function deleteFormat(formatId: number): void {
      patchState(store, { loading: true });
      const formatToDelete = store.entityMap()[formatId];

      patchState(store, removeEntities([formatId]));
      patchState(store, { pagination: { ...store.pagination(), totalCount: store.pagination.totalCount() - 1 } });

      formatService.deleteFormat(formatId).subscribe({
        next: () => {
          patchState(store, { loading: false });

          if (store.pagination().hasNext && store.entities().length < store.pagination().size) {
            // make sure that when a format is deleted, the list is filled up to the batch size if there are more formats available
            loadFormatsBatch({ isFirstLoad: false, removeEntities: false });
          }
        },
        error: error => {
          console.error('FormatStore: error on deleting format', error.error);
          patchState(store, { loading: false });
          addFormat(formatToDelete);
        }
      });
    }

    function reset(isLoading?: boolean): void {
      const loading = isLoading === undefined ? store.loading() : isLoading;
      patchState(store, { pagination: initialState.pagination, firstLoadCompleted: false, loading });
      patchState(store, removeEntities(store.entities().map(entity => entity.id)));
    }

    return {
      addFormat,
      loadFormatById,
      loadFormatsBatch,
      loadNextFormatsBatch,
      updateBatchSize,
      updateSelectedFormatId,
      updateFilters,
      deleteFormat,
      updateFormat,
      reset
    };
  })
);
